import React, { useRef, useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/lib/posthog';
import { Share2, Download, MessageCircle, Facebook, Instagram, Smartphone } from 'lucide-react';
import html2canvas from 'html2canvas';

const translations = {
  fr: {
    before: 'Avant',
    after: 'Après',
    tipsApplied: 'conseils appliqués',
    tipApplied: 'conseil appliqué',
    cta: 'Vérifie ton score → https://cv.edomatch.com',
    glowUp: 'Plus gros glow-up ! 🔥',
    shareTitle: 'Partager mon score',
    whatsapp: 'WhatsApp',
    whatsappStatus: 'WhatsApp Status',
    instagram: 'Instagram Stories',
    facebook: 'Facebook',
    download: 'Télécharger image',
    share: 'Partager',
    generating: 'Génération...',
  },
  en: {
    before: 'Before',
    after: 'After',
    tipsApplied: 'improvement tips applied',
    tipApplied: 'improvement tip applied',
    cta: 'Check your score → https://cv.edomatch.com',
    glowUp: 'Biggest glow-up! 🔥',
    shareTitle: 'Share my score',
    whatsapp: 'WhatsApp',
    whatsappStatus: 'WhatsApp Status',
    instagram: 'Instagram Stories',
    facebook: 'Facebook',
    download: 'Download image',
    share: 'Share',
    generating: 'Generating...',
  }
};

function getShareText(scoreBefore, scoreAfter, language = 'en') {
  const url = 'https://cv.edomatch.com/?utm_source=scorecard&utm_medium=share';
  if (language === 'fr') {
    return `🎯 Mon CV est passé de ${scoreBefore}/10 à ${scoreAfter}/10 grâce à EdoMatch !\n\n💡 Vérifie ton score gratuitement ici 👇\n${url}`;
  }
  return `🎯 My CV went from ${scoreBefore}/10 to ${scoreAfter}/10 thanks to EdoMatch!\n\n💡 Check your score for free here 👇\n${url}`;
}

export default function ScoreCard({ 
  firstName = '', 
  scoreBefore = 0, 
  scoreAfter = 0, 
  tipsCount = 0 
}) {
  const { language } = useLanguage();
  const t = translations[language] || translations.fr;
  const cardRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const delta = scoreAfter - scoreBefore;
  const showGlowUp = delta >= 4;

  const generateImage = async () => {
    setGenerating(true);
    try {
      const W = 600, H = 700;
      const canvas = document.createElement('canvas');
      canvas.width = W * 2;
      canvas.height = H * 2;
      const ctx = canvas.getContext('2d');
      ctx.scale(2, 2);

      // Background gradient
      const bgGrad = ctx.createLinearGradient(0, 0, W, H);
      bgGrad.addColorStop(0, '#0F172A');
      bgGrad.addColorStop(0.5, '#1E293B');
      bgGrad.addColorStop(1, '#0F172A');
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, W, H);

      // Decorative glow
      ctx.globalAlpha = 0.08;
      ctx.fillStyle = '#5BA946';
      ctx.beginPath(); ctx.arc(100, 150, 200, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#FEA501';
      ctx.beginPath(); ctx.arc(500, 500, 180, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = 1;

      // Top accent
      const accentGrad = ctx.createLinearGradient(100, 0, W - 100, 0);
      accentGrad.addColorStop(0, '#5BA946');
      accentGrad.addColorStop(0.5, '#3DC73E');
      accentGrad.addColorStop(1, '#FEA501');
      ctx.fillStyle = accentGrad;
      ctx.beginPath(); ctx.roundRect(W / 2 - 150, 30, 300, 4, 2); ctx.fill();

      // Logo
      try {
        const logo = new Image();
        logo.crossOrigin = 'anonymous';
        logo.src = '/favicon-edomatch.png';
        await new Promise((resolve, reject) => { logo.onload = resolve; logo.onerror = reject; setTimeout(reject, 2000); });
        ctx.drawImage(logo, W / 2 - 22, 55, 44, 44);
      } catch {}
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 16px -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('EDOMATCH', W / 2, 120);

      let y = 155;
      if (firstName) {
        ctx.fillStyle = '#94A3B8';
        ctx.font = '15px -apple-system, sans-serif';
        ctx.fillText(firstName, W / 2, y);
        y += 35;
      }

      // Glow-up badge
      if (showGlowUp) {
        const grad = ctx.createLinearGradient(W / 2 - 90, y, W / 2 + 90, y);
        grad.addColorStop(0, '#FEA501');
        grad.addColorStop(1, '#3DC73E');
        ctx.fillStyle = grad;
        ctx.beginPath(); ctx.roundRect(W / 2 - 90, y - 12, 180, 28, 14); ctx.fill();
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 12px -apple-system, sans-serif';
        ctx.fillText(t.glowUp, W / 2, y + 6);
        y += 45;
      }

      function getColor(s) {
        if (s >= 9) return '#FFD700';
        if (s >= 7) return '#3DC73E';
        if (s >= 5) return '#FEA501';
        return '#E53E3E';
      }

      // Score card backgrounds
      const cardY = y;
      ctx.fillStyle = 'rgba(255,255,255,0.05)';
      ctx.beginPath(); ctx.roundRect(W / 2 - 200, cardY - 10, 170, 140, 16); ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.1)'; ctx.lineWidth = 1; ctx.stroke();
      ctx.fillStyle = 'rgba(255,255,255,0.05)';
      ctx.beginPath(); ctx.roundRect(W / 2 + 30, cardY - 10, 170, 140, 16); ctx.fill();
      ctx.strokeStyle = 'rgba(91,169,70,0.3)'; ctx.stroke();

      ctx.fillStyle = '#64748B';
      ctx.font = '11px -apple-system, sans-serif';
      ctx.fillText(t.before.toUpperCase(), W / 2 - 115, cardY + 18);
      ctx.fillText(t.after.toUpperCase(), W / 2 + 115, cardY + 18);

      ctx.font = 'bold 64px -apple-system, sans-serif';
      ctx.fillStyle = getColor(scoreBefore);
      ctx.fillText(String(scoreBefore), W / 2 - 130, cardY + 90);
      ctx.fillStyle = '#475569'; ctx.font = '22px -apple-system, sans-serif';
      ctx.fillText('/10', W / 2 - 80, cardY + 90);

      const arrowGrad = ctx.createLinearGradient(W / 2 - 20, cardY + 60, W / 2 + 20, cardY + 60);
      arrowGrad.addColorStop(0, '#FEA501'); arrowGrad.addColorStop(1, '#3DC73E');
      ctx.fillStyle = arrowGrad;
      ctx.font = 'bold 28px -apple-system, sans-serif';
      ctx.fillText('→', W / 2, cardY + 75);

      ctx.font = 'bold 64px -apple-system, sans-serif';
      ctx.fillStyle = getColor(scoreAfter);
      ctx.fillText(String(scoreAfter), W / 2 + 100, cardY + 90);
      ctx.fillStyle = '#475569'; ctx.font = '22px -apple-system, sans-serif';
      ctx.fillText('/10', W / 2 + 150, cardY + 90);

      y = cardY + 165;
      if (tipsCount > 0) {
        ctx.fillStyle = '#5BA946';
        ctx.font = 'bold 14px -apple-system, sans-serif';
        ctx.fillText(`✅ ${tipsCount} ${tipsCount === 1 ? t.tipApplied : t.tipsApplied}`, W / 2, y);
        y += 40;
      }

      // Progress bar
      const barW = 300, barH = 8, barX = W / 2 - barW / 2;
      ctx.fillStyle = 'rgba(255,255,255,0.1)';
      ctx.beginPath(); ctx.roundRect(barX, y, barW, barH, 4); ctx.fill();
      const fillW = (scoreAfter / 10) * barW;
      const barGrad = ctx.createLinearGradient(barX, y, barX + fillW, y);
      barGrad.addColorStop(0, '#5BA946'); barGrad.addColorStop(1, '#3DC73E');
      ctx.fillStyle = barGrad;
      ctx.beginPath(); ctx.roundRect(barX, y, fillW, barH, 4); ctx.fill();
      y += 35;

      // CTA pill
      ctx.fillStyle = 'rgba(91,169,70,0.15)';
      ctx.beginPath(); ctx.roundRect(W / 2 - 170, y - 5, 340, 36, 18); ctx.fill();
      ctx.strokeStyle = 'rgba(91,169,70,0.3)'; ctx.lineWidth = 1; ctx.stroke();
      ctx.fillStyle = '#5BA946';
      ctx.font = 'bold 13px -apple-system, sans-serif';
      ctx.fillText(t.cta, W / 2, y + 18);

      // Bottom accent
      ctx.fillStyle = accentGrad;
      ctx.beginPath(); ctx.roundRect(W / 2 - 100, H - 25, 200, 3, 2); ctx.fill();

      setGenerating(false);
      return canvas.toDataURL('image/png');
    } catch (err) {
      console.error('Card generation error:', err);
      setGenerating(false);
      return null;
    }
  };

  const handleDownload = async () => {
    const dataUrl = await generateImage();
    if (!dataUrl) return;
    const link = document.createElement('a');
    link.download = `edomatch-score-${firstName || 'card'}.png`;
    link.href = dataUrl;
    link.click();
    trackEvent('score_shared', { channel: 'download', score_before: scoreBefore, score_after: scoreAfter });
  };

  const isMobile = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const handleWhatsApp = async () => {
    const dataUrl = await generateImage();
    
    if (isMobile && dataUrl && navigator.share && navigator.canShare) {
      // MOBILE: native share sheet with image
      try {
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        const file = new File([blob], 'edomatch-score.png', { type: 'image/png' });
        const shareData = { text: getShareText(scoreBefore, scoreAfter, language), files: [file] };
        if (navigator.canShare(shareData)) {
          await navigator.share(shareData);
          trackEvent('score_shared', { channel: 'whatsapp_mobile_image', score_before: scoreBefore, score_after: scoreAfter });
          return;
        }
      } catch (err) {
        if (err.name !== 'AbortError') console.log('Native share failed:', err);
      }
    }
    
    // DESKTOP: open WhatsApp Web first, then download image
    const text = encodeURIComponent(getShareText(scoreBefore, scoreAfter, language));
    window.open(`https://wa.me/?text=${text}`, '_blank');
    if (dataUrl) {
      setTimeout(() => {
        const link = document.createElement('a');
        link.download = 'edomatch-score.png';
        link.href = dataUrl;
        link.click();
      }, 500);
    }
    trackEvent('score_shared', { channel: isMobile ? 'whatsapp_mobile' : 'whatsapp_desktop', score_before: scoreBefore, score_after: scoreAfter });
  };

  const handleWhatsAppStatus = async () => {
    // Download image, then open WhatsApp so user can share to status
    await handleDownload();
    // Small delay then nudge to WhatsApp
    setTimeout(() => {
      const text = encodeURIComponent(getShareText(scoreBefore, scoreAfter, language));
      window.open(`https://wa.me/?text=${text}`, '_blank');
    }, 500);
    trackEvent('score_shared', { channel: 'whatsapp_status', score_before: scoreBefore, score_after: scoreAfter });
  };

  const handleInstagram = async () => {
    // Download image first, user shares to stories manually
    await handleDownload();
    trackEvent('score_shared', { channel: 'instagram_stories', score_before: scoreBefore, score_after: scoreAfter });
  };

  const handleFacebook = () => {
    const url = encodeURIComponent('https://cv.edomatch.com/?utm_source=scorecard&utm_medium=share');
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
    trackEvent('score_shared', { channel: 'facebook', score_before: scoreBefore, score_after: scoreAfter });
  };

  const handleNativeShare = async () => {
    const dataUrl = await generateImage();
    if (!dataUrl || !navigator.share) return;
    try {
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], 'edomatch-score.png', { type: 'image/png' });
      await navigator.share({
        title: 'EdoMatch Score',
        text: getShareText(scoreBefore, scoreAfter, language),
        files: [file],
      });
      trackEvent('score_shared', { channel: 'native_share', score_before: scoreBefore, score_after: scoreAfter });
    } catch (err) {
      // User cancelled or not supported
      console.log('Share cancelled:', err);
    }
  };

  const scoreColor = (s) => s >= 9 ? '#FFD700' : s >= 7 ? '#3DC73E' : s >= 5 ? '#FEA501' : '#E53E3E';

  return (
    <div className="w-full max-w-sm mx-auto">
      {/* The shareable card */}
      <div
        ref={cardRef}
        className="relative overflow-hidden rounded-2xl p-6"
        style={{
          background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)',
          aspectRatio: '6/7',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Decorative glows */}
        <div className="absolute top-0 left-0 w-48 h-48 rounded-full opacity-[0.06]" style={{ background: '#5BA946', filter: 'blur(60px)' }} />
        <div className="absolute bottom-0 right-0 w-40 h-40 rounded-full opacity-[0.06]" style={{ background: '#FEA501', filter: 'blur(60px)' }} />

        {/* Top accent line */}
        <div className="absolute top-6 left-1/2 -translate-x-1/2 w-40 h-1 rounded-full bg-gradient-to-r from-[#5BA946] via-[#3DC73E] to-[#FEA501]" />

        {/* Logo */}
        <img src="/favicon-edomatch.png" alt="EdoMatch" className="w-10 h-10 mb-1.5 relative z-10" crossOrigin="anonymous" />
        <span className="text-white font-bold text-sm tracking-[0.2em] mb-4 opacity-80 relative z-10">EDOMATCH</span>

        {/* Name */}
        {firstName && <p className="text-slate-400 text-sm mb-2 relative z-10">{firstName}</p>}

        {/* Glow-up badge */}
        {showGlowUp && (
          <div className="bg-gradient-to-r from-[#FEA501] to-[#3DC73E] text-white text-xs font-bold px-5 py-1.5 rounded-full mb-4 relative z-10">
            {t.glowUp}
          </div>
        )}

        {/* Score cards */}
        <div className="flex items-center gap-3 mb-5 relative z-10">
          {/* Before card */}
          <div className="bg-white/5 border border-white/10 rounded-xl px-5 py-3 text-center min-w-[100px]">
            <p className="text-slate-500 text-[10px] uppercase tracking-widest mb-1">{t.before}</p>
            <span className="text-5xl font-black" style={{ color: scoreColor(scoreBefore) }}>{scoreBefore}</span>
            <span className="text-slate-500 text-lg">/10</span>
          </div>

          {/* Gradient arrow */}
          <div className="text-2xl font-bold bg-gradient-to-r from-[#FEA501] to-[#3DC73E] bg-clip-text text-transparent">→</div>

          {/* After card */}
          <div className="bg-white/5 border border-[#5BA946]/30 rounded-xl px-5 py-3 text-center min-w-[100px]">
            <p className="text-slate-500 text-[10px] uppercase tracking-widest mb-1">{t.after}</p>
            <span className="text-5xl font-black" style={{ color: scoreColor(scoreAfter) }}>{scoreAfter}</span>
            <span className="text-slate-500 text-lg">/10</span>
          </div>
        </div>

        {/* Tips count */}
        {tipsCount > 0 && (
          <p className="text-[#5BA946] text-sm font-semibold mb-4 relative z-10">
            ✅ {tipsCount} {tipsCount === 1 ? t.tipApplied : t.tipsApplied}
          </p>
        )}

        {/* Progress bar */}
        <div className="w-64 h-1.5 bg-white/10 rounded-full mb-4 overflow-hidden relative z-10">
          <div className="h-full rounded-full bg-gradient-to-r from-[#5BA946] to-[#3DC73E]" style={{ width: `${(scoreAfter / 10) * 100}%` }} />
        </div>

        {/* CTA pill */}
        <div className="bg-[#5BA946]/15 border border-[#5BA946]/30 rounded-full px-5 py-2 relative z-10">
          <p className="text-[#5BA946] text-xs font-semibold text-center">💡 {t.cta}</p>
        </div>

        {/* Bottom accent line */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 w-28 h-0.5 rounded-full bg-gradient-to-r from-[#5BA946] via-[#3DC73E] to-[#FEA501]" />
      </div>

      {/* Share buttons */}
      <div className="mt-4 space-y-2">
        <p className="text-gray-400 text-sm font-medium text-center">{t.shareTitle}</p>
        
        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={handleWhatsApp}
            className="bg-[#25D366] hover:bg-[#20bd5a] text-white gap-2 text-sm"
            size="sm"
          >
            <MessageCircle className="w-4 h-4" />
            {t.whatsapp}
          </Button>
          
          <Button
            onClick={handleWhatsAppStatus}
            className="bg-[#25D366]/80 hover:bg-[#25D366] text-white gap-2 text-sm"
            size="sm"
          >
            <Smartphone className="w-4 h-4" />
            {t.whatsappStatus}
          </Button>
          
          <Button
            onClick={handleInstagram}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white gap-2 text-sm"
            size="sm"
          >
            <Instagram className="w-4 h-4" />
            {t.instagram}
          </Button>
          
          <Button
            onClick={handleFacebook}
            className="bg-[#1877F2] hover:bg-[#166fe5] text-white gap-2 text-sm"
            size="sm"
          >
            <Facebook className="w-4 h-4" />
            {t.facebook}
          </Button>
          
          <Button
            onClick={handleDownload}
            disabled={generating}
            variant="outline"
            className="gap-2 text-sm border-gray-600 text-gray-300 hover:bg-gray-800"
            size="sm"
          >
            <Download className="w-4 h-4" />
            {generating ? t.generating : t.download}
          </Button>
          
          {typeof navigator !== 'undefined' && navigator.share && (
            <Button
              onClick={handleNativeShare}
              disabled={generating}
              className="bg-[#3DC73E] hover:bg-[#35b336] text-white gap-2 text-sm"
              size="sm"
            >
              <Share2 className="w-4 h-4" />
              {t.share}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
